/**
 * Structured logger for React frontend
 *
 * Provides environment-aware logging with different log levels.
 * In production, only logs warnings and errors.
 * In development, logs everything for debugging.
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

interface LogContext {
  [key: string]: any;
}

class Logger {
  private readonly environment: string;
  private readonly minLevel: LogLevel;

  constructor() {
    this.environment = process.env.NODE_ENV || 'development';
    this.minLevel = this.environment === 'production' ? LogLevel.WARN : LogLevel.DEBUG;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(level);
    const minLevelIndex = levels.indexOf(this.minLevel);
    return currentLevelIndex >= minLevelIndex;
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string[] {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level}]`;
    const parts: string[] = [prefix, message];
    if (context) {
      parts.push(JSON.stringify(context, null, 2));
    }
    return parts;
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.formatMessage(LogLevel.DEBUG, message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(...this.formatMessage(LogLevel.INFO, message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage(LogLevel.WARN, message, context));
    }
  }

  error(message: string, error?: Error | any, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorContext = {
        ...context,
        ...(error instanceof Error && {
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack
          }
        }),
        ...(!(error instanceof Error) && error && { details: error })
      };
      console.error(...this.formatMessage(LogLevel.ERROR, message, errorContext));
    }
  }
}

// Export singleton instance
export const logger = new Logger();
